<template>
  <div class="week-guidance week-group__guidance">
    <div v-if="!props.isExpanded" class="week-guidance__display">
      <template v-if="props.modelValue">
        <IconEmbedded name="bulb_2-5" :size="16" class="week-guidance__icon" />
        <span class="week-guidance__text global-p">{{ props.modelValue }}</span>
        <LinkWithIcon isDottedUnderline color="gray" class="week-guidance__edit-link" @click.prevent="emit('open')">
          <template #icon><IconEmbedded name="pencil_2" :size="14" /></template>
          <span></span>
        </LinkWithIcon>
      </template>
      <LinkWithIcon v-else isDottedUnderline color="blue" @click.prevent="emit('open')">
        <template #icon><IconEmbedded name="bulb_2-5" :size="16" /></template>
        <span>Add Guidance</span>
      </LinkWithIcon>
      <SrpButton
        v-if="props.showDebugAction"
        size="tiny"
        color="superonly"
        fill="outlined"
        class="week-guidance__debug-btn"
        :isDisabled="props.isDebugActionLoading"
        @click="emit('triggerPostGen', props.weekKey)"
      >
        <template #icon>
          <LoadingIcon v-if="props.isDebugActionLoading" :size="12" />
          <IconEmbedded v-else name="reload_2-5" :size="12" />
        </template>
        Gen posts
      </SrpButton>

      <div v-if="props.showDebugAction" class="week-guidance__system global-p"><b>System:</b> {{ props.systemTopics }}</div>
    </div>
    <div v-else class="week-guidance__edit">
      <textarea
        ref="textareaRef"
        :value="props.modelValue"
        :data-week="props.weekKey"
        @input="emit('update:modelValue', ($event.target as HTMLTextAreaElement).value)"
        @blur="emit('blur')"
        placeholder="What should we highlight in this week's posts?"
        class="week-guidance__input global-p"
        :disabled="props.isSaving"
        rows="1"
      />
      <LoadingIcon v-if="props.isSaving" :size="14" class="week-guidance__loading" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import LoadingIcon from "@components/ui/LoadingIcon.vue";
import SrpButton from "@components/ui/SrpButton.vue";

const props = withDefaults(
  defineProps<{
    weekKey: string;
    modelValue: string;
    systemTopics?: string | null;
    isExpanded: boolean;
    isSaving: boolean;
    showDebugAction?: boolean;
    isDebugActionLoading?: boolean;
  }>(),
  { showDebugAction: false, isDebugActionLoading: false, systemTopics: undefined }
);

const emit = defineEmits<{
  open: [];
  "update:modelValue": [value: string];
  blur: [];
  triggerPostGen: [weekKey: string];
}>();

const textareaRef = ref<HTMLTextAreaElement | null>(null);

watch(
  () => props.isExpanded,
  expanded => {
    if (expanded) {
      nextTick(() => {
        const input = textareaRef.value;
        if (!input) return;
        input.focus();
        const cursorPos = input.value?.length ?? 0;
        input.setSelectionRange(cursorPos, cursorPos);
      });
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
.week-guidance {
  &__display {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__icon {
    flex-shrink: 0;
    margin-top: 2px;
    opacity: 0.7;
  }

  &__text {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }

  &__edit-link {
    flex-shrink: 0;
  }

  &__system {
    flex-basis: 100%;
    margin: 4px 0 0;
    font-size: 0.9em;
    opacity: 0.85;
  }

  &__debug-btn {
    flex-shrink: 0;
    margin-left: auto;
  }

  &__edit {
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  &__input {
    flex: 1;
    min-width: 0;
    max-width: 600px;
    resize: vertical;
    padding: 4px 8px;
    border: 1px rgba(0, 0, 0, 0.15) solid;
    border-radius: 4px;
    font-size: inherit;
    line-height: 1.25;
    height: 50px;

    &::placeholder {
      color: rgba(0, 0, 0, 0.4);
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  &__loading {
    flex-shrink: 0;
    margin-top: 6px;
  }
}

.week-group__guidance {
  margin-bottom: 16px;
}
</style>
